if (!customElements.get('lpr-load-more')) {
  class LprLoadMore extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';

      this.button = this.querySelector('[data-load-more-button]');
      this.buttonText = this.querySelector('[data-load-more-text]');
      this.spinner = this.querySelector('[data-load-more-spinner]');
      this.errorMessage = this.querySelector('[data-load-more-error]');

      this.grid =
        this.closest('#ProductGridContainer')?.querySelector('#product-grid') ||
        document.querySelector('#product-grid');

      this.entryId = this.ensureHistoryEntryId();
      this.nextUrl = this.dataset.nextUrl || '';
      this.loadedPages = 1;
      this.hasReachedEnd = false;
      this.isLoading = false;
      this.isRestoring = false;
      this.restoreStarted = false;
      this.lastProductPath = '';
      this.lastProductViewportTop = null;

      /*
       * Empêche le navigateur mobile et le script de tenter
       * de restaurer la position en même temps.
       */
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual';
      }

      this.handleClick = this.loadMore.bind(this);
      this.handlePageHide = this.onPageHide.bind(this);
      this.handlePageShow = this.onPageShow.bind(this);
      this.handleGridClick = this.saveStateBeforeProductVisit.bind(this);

      if (this.button) {
        this.button.addEventListener('click', this.handleClick);
      }

      if (this.grid) {
        this.grid.addEventListener('click', this.handleGridClick);
      }

      window.addEventListener('pagehide', this.handlePageHide);
      window.addEventListener('pageshow', this.handlePageShow);

      const fallbackPagination = this.parentElement?.querySelector(
        '[data-pagination-fallback]'
      );

      if (fallbackPagination) {
        fallbackPagination.hidden = true;
      }

      const savedState = this.getSavedState();

      if (savedState?.hasReachedEnd) {
        this.hasReachedEnd = true;
        this.hidden = true;
      } else {
        this.hidden = false;
      }

      if (savedState?.pendingReturn) {
        window.setTimeout(() => {
          this.startRestore(savedState);
        }, 0);
      }
    }

    disconnectedCallback() {
      if (this.button && this.handleClick) {
        this.button.removeEventListener('click', this.handleClick);
      }

      if (this.grid && this.handleGridClick) {
        this.grid.removeEventListener('click', this.handleGridClick);
      }

      window.removeEventListener('pagehide', this.handlePageHide);
      window.removeEventListener('pageshow', this.handlePageShow);
    }

    ensureHistoryEntryId() {
      const existingEntryId = history.state?.lprCollectionEntryId;

      if (existingEntryId) return existingEntryId;

      const newEntryId = `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`;

      history.replaceState(
        {
          ...history.state,
          lprCollectionEntryId: newEntryId,
        },
        document.title,
        window.location.href
      );

      return newEntryId;
    }

    getStorageKey() {
      const url = new URL(window.location.href);

      url.searchParams.delete('page');
      url.searchParams.delete('section_id');
      url.searchParams.delete('sections');

      const sortedParameters = Array.from(
        url.searchParams.entries()
      ).sort((firstParameter, secondParameter) => {
        const firstValue = `${firstParameter[0]}=${firstParameter[1]}`;
        const secondValue = `${secondParameter[0]}=${secondParameter[1]}`;

        return firstValue.localeCompare(secondValue);
      });

      const normalizedParameters = new URLSearchParams(
        sortedParameters
      ).toString();

      return `lpr-load-more:${this.entryId}:${url.pathname}${
        normalizedParameters ? `?${normalizedParameters}` : ''
      }`;
    }

    getSavedState() {
      try {
        const storageKey = this.getStorageKey();
        const historyState = history.state?.lprLoadMore;

        let state = null;

        if (
          historyState?.storageKey === storageKey &&
          historyState?.entryId === this.entryId
        ) {
          state = historyState;
        } else {
          const savedValue = sessionStorage.getItem(storageKey);

          state = savedValue ? JSON.parse(savedValue) : null;
        }

        if (!state) return null;

        const maximumAge = 2 * 60 * 60 * 1000;

        const stateIsExpired =
          !state.timestamp ||
          Date.now() - state.timestamp > maximumAge;

        if (
          stateIsExpired ||
          state.entryId !== this.entryId ||
          state.storageKey !== storageKey ||
          !Number.isInteger(state.loadedPages) ||
          state.loadedPages < 1
        ) {
          sessionStorage.removeItem(storageKey);

          return null;
        }

        return state;
      } catch (error) {
        console.warn(
          'Le Prix Royal — état de collection introuvable :',
          error
        );

        return null;
      }
    }

    saveState(options = {}) {
      if (this.isRestoring && options.force !== true) return;

      try {
        const existingState = this.getSavedState();
        const storageKey = this.getStorageKey();

        const pendingReturn =
          typeof options.pendingReturn === 'boolean'
            ? options.pendingReturn
            : existingState?.pendingReturn || false;

        const productPath =
          this.lastProductPath || existingState?.productPath || '';

        const productViewportTop = Number.isFinite(
          this.lastProductViewportTop
        )
          ? this.lastProductViewportTop
          : Number.isFinite(existingState?.productViewportTop)
            ? existingState.productViewportTop
            : null;

        const state = {
          storageKey,
          entryId: this.entryId,
          loadedPages: this.loadedPages,
          hasReachedEnd: this.hasReachedEnd || !this.nextUrl,
          scrollY: Math.max(window.scrollY, 0),
          productPath,
          productViewportTop,
          pendingReturn,
          timestamp: Date.now(),
        };

        sessionStorage.setItem(storageKey, JSON.stringify(state));

        history.replaceState(
          {
            ...history.state,
            lprCollectionEntryId: this.entryId,
            lprLoadMore: state,
          },
          document.title,
          window.location.href
        );
      } catch (error) {
        console.warn(
          'Le Prix Royal — état de collection non enregistré :',
          error
        );
      }
    }

    onPageHide() {
      this.saveState();
    }

    onPageShow() {
      const savedState = this.getSavedState();

      if (!savedState?.pendingReturn) return;

      this.startRestore(savedState);
    }

    startRestore(savedState) {
      if (this.restoreStarted || this.isRestoring) return;

      this.restoreStarted = true;

      this.restorePreviousState(savedState);
    }

    saveStateBeforeProductVisit(event) {
      const productLink = event.target.closest(
        'a[href*="/products/"]'
      );

      if (!productLink) return;

      const productCard =
        productLink.closest('li.grid__item') || productLink;

      try {
        const productUrl = new URL(
          productLink.href,
          window.location.origin
        );

        this.lastProductPath = this.normalizePath(
          productUrl.pathname
        );

        this.lastProductViewportTop =
          productCard.getBoundingClientRect().top;
      } catch (error) {
        this.lastProductPath = '';
        this.lastProductViewportTop = null;
      }

      /*
       * Autorise une nouvelle restauration même si une restauration
       * précédente a déjà été effectuée dans cette page.
       */
      this.restoreStarted = false;

      this.saveState({
        pendingReturn: true,
      });
    }

    normalizePath(pathname) {
      return pathname.replace(/\/+$/, '');
    }

    findSavedProductCard(productPath) {
      if (!productPath || !this.grid) return null;

      const normalizedSavedPath = this.normalizePath(productPath);

      const productLinks = this.grid.querySelectorAll(
        'a[href*="/products/"]'
      );

      for (const productLink of productLinks) {
        try {
          const productUrl = new URL(
            productLink.href,
            window.location.origin
          );

          if (
            this.normalizePath(productUrl.pathname) ===
            normalizedSavedPath
          ) {
            return (
              productLink.closest('li.grid__item') ||
              productLink
            );
          }
        } catch (error) {
          /*
           * Ignore uniquement ce lien invalide.
           */
        }
      }

      return null;
    }

    async restorePreviousState(state) {
      this.isRestoring = true;
      this.hideError();

      if (!state.hasReachedEnd) {
        this.hidden = false;

        this.setLoadingState(true, 'Restauration…');
      }

      try {
        while (
          this.loadedPages < state.loadedPages &&
          this.nextUrl
        ) {
          const productsWereAdded = await this.appendNextPage();

          if (!productsWereAdded) {
            this.finishLoading();
            break;
          }

          this.loadedPages += 1;
        }

        if (state.hasReachedEnd || !this.nextUrl) {
          this.finishLoading();
        }

        this.lastProductPath = state.productPath || '';

        this.lastProductViewportTop = Number.isFinite(
          state.productViewportTop
        )
          ? state.productViewportTop
          : null;

        await this.restoreCustomerPosition(state);

        this.saveState({
          pendingReturn: false,
          force: true,
        });
      } catch (error) {
        console.error(
          'Le Prix Royal — restauration de la collection :',
          error
        );

        this.showError();
      } finally {
        this.isRestoring = false;
        this.restoreStarted = false;

        if (
          document.body.contains(this) &&
          !this.hasReachedEnd
        ) {
          this.setLoadingState(false);
        }
      }
    }

    async restoreCustomerPosition(state) {
      await this.waitForLayout();

      const savedProductCard = this.findSavedProductCard(
        state.productPath
      );

      let customerStartedScrolling = false;
      let scriptIsPositioning = false;

      const detectCustomerInteraction = () => {
        if (!scriptIsPositioning) {
          customerStartedScrolling = true;
        }
      };

      const interactionEvents = [
        'touchstart',
        'pointerdown',
        'wheel',
        'keydown',
      ];

      interactionEvents.forEach((eventName) => {
        window.addEventListener(
          eventName,
          detectCustomerInteraction,
          {
            passive: true,
            once: true,
          }
        );
      });

      const positionCustomer = () => {
        if (customerStartedScrolling) return;

        let targetScrollPosition = null;

        if (savedProductCard) {
          const currentCardTop =
            savedProductCard.getBoundingClientRect().top;

          const savedTopIsUsable =
            Number.isFinite(state.productViewportTop) &&
            state.productViewportTop > -100 &&
            state.productViewportTop < window.innerHeight - 80;

          const desiredViewportTop = savedTopIsUsable
            ? state.productViewportTop
            : Math.max(window.innerHeight * 0.28, 90);

          targetScrollPosition =
            window.scrollY +
            currentCardTop -
            desiredViewportTop;
        } else if (Number.isFinite(state.scrollY)) {
          targetScrollPosition = state.scrollY;
        }

        if (!Number.isFinite(targetScrollPosition)) return;

        const distance = Math.abs(
          window.scrollY - targetScrollPosition
        );

        /*
         * Ignore les très petites corrections qui peuvent
         * donner une impression de tremblement.
         */
        if (distance < 12) return;

        scriptIsPositioning = true;

        window.scrollTo({
          top: Math.max(targetScrollPosition, 0),
          left: 0,
          behavior: 'auto',
        });

        requestAnimationFrame(() => {
          scriptIsPositioning = false;
        });
      };

      try {
        positionCustomer();

        await this.delay(100);
        positionCustomer();

        if (savedProductCard && !customerStartedScrolling) {
          await this.waitForImages(savedProductCard, 700);
          positionCustomer();
        }

        await this.delay(250);
        positionCustomer();
      } finally {
        interactionEvents.forEach((eventName) => {
          window.removeEventListener(
            eventName,
            detectCustomerInteraction
          );
        });
      }
    }

    async waitForLayout() {
      if (document.fonts?.ready) {
        try {
          await document.fonts.ready;
        } catch (error) {
          /*
           * Continue même si une police ne se charge pas.
           */
        }
      }

      await new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(resolve);
        });
      });
    }

    async waitForImages(element, maximumWait) {
      const images = Array.from(
        element.querySelectorAll('img')
      );

      if (images.length === 0) return;

      const imagePromises = images.map((image) => {
        if (image.complete) {
          if (typeof image.decode === 'function') {
            return image.decode().catch(() => undefined);
          }

          return Promise.resolve();
        }

        return new Promise((resolve) => {
          const finish = () => resolve();

          image.addEventListener('load', finish, {
            once: true,
          });

          image.addEventListener('error', finish, {
            once: true,
          });
        });
      });

      await Promise.race([
        Promise.allSettled(imagePromises),
        this.delay(maximumWait),
      ]);
    }

    delay(milliseconds) {
      return new Promise((resolve) => {
        window.setTimeout(resolve, milliseconds);
      });
    }

    async loadMore() {
      if (this.isLoading || !this.nextUrl) {
        if (!this.nextUrl) {
          this.finishLoading();

          this.saveState({
            pendingReturn: false,
          });
        }

        return;
      }

      this.setLoadingState(true);
      this.hideError();

      try {
        const productsWereAdded = await this.appendNextPage();

        if (!productsWereAdded) {
          this.finishLoading();

          this.saveState({
            pendingReturn: false,
          });

          return;
        }

        this.loadedPages += 1;

        if (!this.nextUrl) {
          this.finishLoading();
        }

        this.saveState({
          pendingReturn: false,
        });
      } catch (error) {
        console.error(
          'Le Prix Royal — chargement des produits :',
          error
        );

        this.showError();
      } finally {
        if (
          document.body.contains(this) &&
          !this.hasReachedEnd
        ) {
          this.setLoadingState(false);
        }
      }
    }

    async appendNextPage() {
      if (!this.nextUrl || !this.grid) return false;

      const response = await fetch(this.nextUrl, {
        method: 'GET',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP : ${response.status}`);
      }

      const htmlText = await response.text();
      const parser = new DOMParser();

      const nextDocument = parser.parseFromString(
        htmlText,
        'text/html'
      );

      const nextGrid = nextDocument.querySelector(
        '#ProductGridContainer #product-grid'
      );

      const nextLoadMore =
        nextDocument.querySelector('lpr-load-more');

      if (!nextGrid) {
        throw new Error(
          'La prochaine grille de produits est introuvable.'
        );
      }

      const newProducts = Array.from(nextGrid.children);

      if (newProducts.length === 0) {
        this.nextUrl = '';
        this.dataset.nextUrl = '';

        return false;
      }

      const fragment = document.createDocumentFragment();

      newProducts.forEach((productCard) => {
        productCard.classList.remove(
          'scroll-trigger',
          'animate--slide-in'
        );

        productCard.removeAttribute('data-cascade');
        productCard.style.removeProperty('--animation-order');

        fragment.appendChild(productCard);
      });

      this.grid.appendChild(fragment);

      this.nextUrl = nextLoadMore?.dataset.nextUrl || '';
      this.dataset.nextUrl = this.nextUrl;

      return true;
    }

    finishLoading() {
      this.nextUrl = '';
      this.dataset.nextUrl = '';
      this.hasReachedEnd = true;
      this.isLoading = false;
      this.hidden = true;

      if (this.button) {
        this.button.disabled = true;
        this.button.setAttribute('aria-busy', 'false');
      }

      if (this.spinner) {
        this.spinner.hidden = true;
      }
    }

    setLoadingState(isLoading, customText = '') {
      this.isLoading = isLoading;

      if (!this.button) return;

      this.button.disabled = isLoading;

      this.button.setAttribute(
        'aria-busy',
        isLoading ? 'true' : 'false'
      );

      if (this.buttonText) {
        this.buttonText.textContent = isLoading
          ? customText || 'Chargement…'
          : 'Afficher plus de produits';
      }

      if (this.spinner) {
        this.spinner.hidden = !isLoading;
      }
    }

    showError() {
      if (this.errorMessage) {
        this.errorMessage.hidden = false;
      }
    }

    hideError() {
      if (this.errorMessage) {
        this.errorMessage.hidden = true;
      }
    }
  }

  customElements.define('lpr-load-more', LprLoadMore);
}